import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heading } from "components/ui/Text";
import { ApiAdminUser } from "utils/api/admin/user";
import { formatCurrency } from "utils/string";
import { isDefined } from "utils/object";
import {
  getAdminUser,
  usePostAdminUserRefreshEligibilityMutation,
} from "store/admin/api/apiSlice";
import { LinkButton } from "components/ui/Link";
import Icon from "components/ui/Icon";
import StatusTag from "../../components/StatusTag";
import ValueWithColor from "../../components/ValueWithColor";
import EligibilityCriteria from "./EligibilityCriteria";
import TaxReturnStatusTag from "../../components/TaxReturnStatusTag";
import EligibilityTag from "../../components/EligibilityTag";
import * as Styled from "./styled";

interface Props {
  user: ApiAdminUser;
}

const SpendingLimit: React.FC<Props> = ({ user }) => {
  const [showEligibilityCriteria, setShowEligibilityCriteria] = useState(false);
  const [refreshEligibility, { isLoading: isRefreshingEligibility }] =
    usePostAdminUserRefreshEligibilityMutation();
  const { isLoading: isLoadingUser } = getAdminUser(user.id);

  const onClickRefreshEligibility = () => {
    refreshEligibility(user.id);
  };

  const isLoadingEligibility = isRefreshingEligibility || isLoadingUser;

  const { safeToSpendComponents, incomeTaxReturns } = user || {};
  const {
    safeToSpend,
    outstandingResetTransactions,
    accruedNetEarnings,
    incomeTaxRefundAmount,
    availableBankBalance,
    upcomingExpensesAmount,
    totalExpensesAmount,
  } = safeToSpendComponents || {};

  return (
    <Styled.SectionTile>
      <Styled.Info>
        <Styled.InfoSection>
          <Heading.H4 tag="h2">Dynamic Spending Limit</Heading.H4>
          {safeToSpendComponents && (
            <>
              <ValueWithColor isCurrency value={safeToSpend} />
              {(user.cards || outstandingResetTransactions > 0) && (
                <Styled.InfoItem
                  label="Outstanding Amount"
                  value={formatCurrency(outstandingResetTransactions, true)}
                />
              )}
              <Styled.InfoItem
                label="Accrued Net Earnings"
                value={formatCurrency(accruedNetEarnings, true)}
              />
              {incomeTaxRefundAmount > 0 && (
                <Styled.InfoItem
                  label="Available Tax Refund Advance"
                  value={formatCurrency(incomeTaxRefundAmount, true)}
                />
              )}
              <Styled.InfoItem
                label="Available Bank Balance"
                value={formatCurrency(availableBankBalance, true)}
              />
              <Styled.InfoItem
                label="Upcoming Expenses"
                value={formatCurrency(upcomingExpensesAmount, true)}
              />
              <Styled.InfoItem
                label="Total Expenses"
                value={formatCurrency(totalExpensesAmount, true)}
              />
            </>
          )}

          {incomeTaxReturns?.length > 0 && (
            <>
              {incomeTaxReturns.map((taxReturn) => (
                <Styled.InfoItem
                  key={taxReturn.jurisdiction}
                  label={`${taxReturn.jurisdiction} Tax Return`}
                  value={
                    <Styled.ValueWithStatus>
                      {formatCurrency(taxReturn.refundAmount, true)}{" "}
                      <TaxReturnStatusTag status={taxReturn.taxReturnStatus} />
                      {taxReturn.refundAdvanceEligible && (
                        <EligibilityTag status="green" />
                      )}
                    </Styled.ValueWithStatus>
                  }
                />
              ))}
            </>
          )}

          {user.eligibilityResult && (
            <Styled.InfoItem
              label="Application Status"
              value={
                <Styled.TagWrapper>
                  <StatusTag
                    status={user.eligibilityResult.applicationStatus}
                  />
                </Styled.TagWrapper>
              }
            />
          )}

          {isDefined(user.eligible) && (
            <Styled.InfoItem
              label="Eligible?"
              value={
                <Styled.TagWrapper>
                  <StatusTag status={user.eligible.toString()} />

                  <Styled.InfoItemLinks>
                    <LinkButton
                      onClick={onClickRefreshEligibility}
                      disabled={isLoadingEligibility}
                      color="violet"
                    >
                      {isLoadingEligibility && (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            repeat: Infinity,
                            duration: 2,
                            ease: "linear",
                          }}
                        >
                          <Icon name="refresh" height={16} />
                        </motion.div>
                      )}
                      Refresh
                    </LinkButton>

                    <LinkButton
                      onClick={() =>
                        setShowEligibilityCriteria((show) => !show)
                      }
                      color="violet"
                    >
                      {showEligibilityCriteria ? "Hide" : "Show"} Criteria
                    </LinkButton>
                  </Styled.InfoItemLinks>
                </Styled.TagWrapper>
              }
            />
          )}

          {showEligibilityCriteria && user.eligibilityResult && (
            <EligibilityCriteria criteria={user.eligibilityResult.results} />
          )}
        </Styled.InfoSection>
      </Styled.Info>
    </Styled.SectionTile>
  );
};

export default SpendingLimit;
